/*
 * rebuy-cart-drawer-bridge.js  —  Keeko (trykeeko.com)
 * v3 — fixes scroll lock stuck after closing the drawer.
 *
 * Syncs any AJAX cart mutation (Rebuy bundles, Rebuy upsells, custom JS)
 * into the Shiprocket Smart Cart drawer.
 *
 * Why: Shiprocket Smart Cart only intercepts CLICKS on native
 * form[action^="/cart/add"] submit buttons. It does not patch fetch/XHR,
 * so Rebuy's programmatic /cart/add.js calls never reach it and its
 * in-memory line-item cache goes stale.
 *
 * v2 changes (fixed auto-open on page load):
 *   - Only /cart/add requests are allowed to OPEN the drawer.
 *     /cart/update|change|clear now refresh silently.
 *   - An open requires a TRUSTED user gesture within GESTURE_WINDOW_MS.
 *   - A boot grace period ignores any mutation fired during page init.
 *   - Double-install guard (safe if the snippet is included twice).
 *
 * v3 changes (fixed scroll broken after closing drawer):
 *   - refreshTheme() no longer emits 'render:cart:drawer' on every
 *     mutation. That event was making the THEME's own cart-drawer
 *     component initialize and apply its own scroll-lock — but since
 *     only the Shiprocket frame is ever shown/closed by the user, the
 *     theme drawer's lock never got released. refreshTheme() now only
 *     patches the cart-count badge via /cart.js (no drawer, no lock).
 *   - Added a scroll-lock watchdog: if body/html end up with a stuck
 *     overflow:hidden / position:fixed and no drawer is actually
 *     visible on screen, it force-clears the lock. This is a safety
 *     net regardless of which script caused the lock.
 */
(function () {
  'use strict';

  if (window.__rebuyBridgeInstalled) return;
  window.__rebuyBridgeInstalled = true;

  var CONFIG = {
    OPEN_DRAWER_ON_ADD: true,     // set false if Rebuy should add silently
    ALSO_SYNC_THEME_DRAWER: true, // keep theme cart-count badge in sync too
    GESTURE_WINDOW_MS: 8000,      // add must follow a real click within this
    BOOT_GRACE_MS: 2000,          // ignore opens during page init

    // Selector(s) for your theme's cart-count badge(s). Adjust to match
    // your theme — check header cart icon markup for a data attribute
    // or class. Multiple selectors are fine (comma-separated).
    CART_COUNT_SELECTOR: '[data-cart-count], .cart-count-bubble span',

    // Failsafe: force-clear a stuck scroll lock if no drawer is visible.
    WATCHDOG_ENABLED: true,
    WATCHDOG_INTERVAL_MS: 800,
    // Selectors used to detect "a drawer IS currently visible" — the
    // watchdog will NOT touch scroll lock while any of these are on screen.
    DRAWER_VISIBLE_SELECTORS: '[id*="shiprocket"], [class*="smart-cart"], [class*="cart-drawer"], [class*="cart-drawer" i]',

    DEBUG: false
  };

  // Any cart write -> refresh. Only ADD -> may open.
  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;
  var CART_ADD   = /\/cart\/add(\.js|\.json)?(\?|$)/;

  var timer = null;
  var pendingOpen = false;
  var bootAt = Date.now();
  var lastGestureAt = 0;

  function log() {
    if (CONFIG.DEBUG) console.log.apply(console, ['[rebuy-bridge]'].concat([].slice.call(arguments)));
  }

  /* ---- 0. Track real user intent ---- */
  ['pointerdown', 'mousedown', 'touchstart', 'keydown', 'click'].forEach(function (evt) {
    document.addEventListener(evt, function (e) {
      if (e && e.isTrusted) lastGestureAt = Date.now();
    }, true);
  });

  function userDrivenNow() {
    if (Date.now() - bootAt < CONFIG.BOOT_GRACE_MS) return false;
    if (!lastGestureAt) return false;
    return (Date.now() - lastGestureAt) < CONFIG.GESTURE_WINDOW_MS;
  }

  function refreshShiprocket(open, attempt) {
    attempt = attempt || 0;
    if (!window.shiprocketSmartCartLoaded || typeof window.updateMiniCartSection !== 'function') {
      if (attempt < 25) return setTimeout(function () { refreshShiprocket(open, attempt + 1); }, 200);
      log('Shiprocket Smart Cart never loaded — giving up');
      return;
    }

    Promise.resolve(window.updateMiniCartSection({ via: 'rebuy_bridge' }))
      .then(function () {
        log('Shiprocket drawer refreshed', open ? '(opening)' : '(silent)');
        if (open && typeof window.slideShiprocketSmartCartInFrame === 'function') {
          window.slideShiprocketSmartCartInFrame();
        }
      })
      .catch(function (e) { log('refresh failed', e); });
  }

  /**
   * v3: patches the cart-count badge only. Does NOT emit any
   * drawer-open/render event — that was what triggered the theme's own
   * scroll-lock on a drawer that never visually opened or closed.
   */
  function refreshTheme() {
    if (!CONFIG.ALSO_SYNC_THEME_DRAWER) return;
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var nodes = document.querySelectorAll(CONFIG.CART_COUNT_SELECTOR);
        nodes.forEach ? nodes.forEach(patch) : Array.prototype.forEach.call(nodes, patch);
        function patch(el) { el.textContent = cart.item_count; }
        log('theme cart-count synced ->', cart.item_count);
      })
      .catch(function (e) { log('theme count sync failed', e); });
  }

  /**
   * @param {string} source  label for logging
   * @param {boolean} isAdd  true only for /cart/add or an explicit add event
   */
  function onCartMutated(source, isAdd) {
    // Latch the open decision at the moment of the request, not after the
    // debounce — otherwise the gesture window can expire mid-bundle.
    if (isAdd && CONFIG.OPEN_DRAWER_ON_ADD && userDrivenNow()) pendingOpen = true;

    clearTimeout(timer);
    timer = setTimeout(function () {
      var open = pendingOpen;
      pendingOpen = false;
      log('cart mutated via', source, '| open:', open);
      refreshTheme();
      refreshShiprocket(open);
    }, 250); // debounce: Rebuy bundles fire several adds in a row
  }

  /* ---- 1. fetch interception (Rebuy uses fetch) ---- */
  var nativeFetch = window.fetch;
  if (typeof nativeFetch === 'function') {
    window.fetch = function () {
      var args = arguments;
      var url = '';
      try {
        url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
      } catch (e) {}

      return nativeFetch.apply(this, args).then(function (res) {
        try {
          if (url && CART_WRITE.test(url) && res && res.ok) {
            onCartMutated('fetch:' + url, CART_ADD.test(url));
          }
        } catch (e) {}
        return res;
      });
    };
  }

  /* ---- 2. XHR interception (fallback for older app code) ---- */
  var nativeOpen = XMLHttpRequest.prototype.open;
  var nativeSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__rbUrl = url;
    return nativeOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function () {
    var xhr = this;
    xhr.addEventListener('load', function () {
      try {
        if (xhr.__rbUrl && CART_WRITE.test(xhr.__rbUrl) && xhr.status >= 200 && xhr.status < 300) {
          onCartMutated('xhr:' + xhr.__rbUrl, CART_ADD.test(xhr.__rbUrl));
        }
      } catch (e) {}
    });
    return nativeSend.apply(this, arguments);
  };

  /* ---- 3. Rebuy's own events, as a belt-and-braces layer ---- */
  // Add events may open (still gesture-gated); the rest refresh silently.
  ['rebuy:cart.add', 'rebuy.add'].forEach(function (evt) {
    document.addEventListener(evt, function () { onCartMutated(evt, true); });
  });
  ['rebuy:cart.change', 'rebuy:cart.update', 'rebuy:cart.remove'].forEach(function (evt) {
    document.addEventListener(evt, function () { onCartMutated(evt, false); });
  });

  /* ---- 4. Scroll-lock watchdog (v3 failsafe) ---- */
  // If html/body end up with a stuck overflow:hidden or position:fixed
  // AND no drawer element is actually visible on screen, clear it.
  // This protects against ANY script (ours, Shiprocket's, or the theme's)
  // leaving the lock engaged after a close.
  function isLocked() {
    var htmlOverflow = document.documentElement.style.overflow;
    var bodyStyle = document.body.style;
    return bodyStyle.overflow === 'hidden' ||
           bodyStyle.position === 'fixed' ||
           htmlOverflow === 'hidden';
  }

  function anyDrawerVisible() {
    var nodes = document.querySelectorAll(CONFIG.DRAWER_VISIBLE_SELECTORS);
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].offsetParent !== null) return true;
    }
    return false;
  }

  function clearScrollLock() {
    var restoreY = 0;
    if (document.body.style.position === 'fixed' && document.body.style.top) {
      restoreY = -parseInt(document.body.style.top, 10) || 0;
    }
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.height = '';
    if (restoreY) window.scrollTo(0, restoreY);
    log('scroll-lock watchdog: cleared a stuck lock');
  }

  if (CONFIG.WATCHDOG_ENABLED) {
    setInterval(function () {
      if (isLocked() && !anyDrawerVisible()) clearScrollLock();
    }, CONFIG.WATCHDOG_INTERVAL_MS);
  }

  log('bridge installed (v3)');
})();